/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Instructor institutions service — GET /courses/user/{userId}/institutions
 *
 * @file InstructorInstitutionsService.js
 * @author Gladson Samuel S
 */

import Framework, { BaseService } from '@greenville/framework';
import {
  INSTRUCTOR_INSTITUTIONS_REQUESTED,
  INSTRUCTOR_INSTITUTIONS_FETCHED,
  INSTRUCTOR_INSTITUTIONS_FAILED
} from '../../../common/constants';

/**
 * Filters active institutions and sorts by updatedDate descending.
 *
 * @param {Array} institutions
 * @returns {Array}
 */
export function normalizeInstructorInstitutions(institutions = []) {
  return institutions
    .filter(({ status }) => status === 'active')
    .sort((a, b) => new Date(b.updatedDate) - new Date(a.updatedDate));
}

export default class InstructorInstitutionsService extends BaseService {
  constructor() {
    super(INSTRUCTOR_INSTITUTIONS_REQUESTED, 'institutionsStatus');
  }

  /**
   * Handles instructor institutions service event
   *
   * @param {*} eventName
   * @param {*} eventData
   * @returns
   */
  handleEvent(eventName, eventData = {}) {
    const { userId } = eventData;

    if (!userId) {
      return Promise.reject(new Error('Missing userId for instructor institutions'));
    }

    return this.getClient('marin').get(`/courses/user/${userId}/institutions`);
  }

  /**
   * Handles instructor institutions service response
   *
   * @param {*} response
   * @param {*} eventData
   */
  // eslint-disable-next-line class-methods-use-this
  handleResponse(response) {
    const institutions = normalizeInstructorInstitutions(response?.data || []);

    Framework.getEventManager().publish(INSTRUCTOR_INSTITUTIONS_FETCHED, {
      institutions
    });
  }

  /**
   * Handles instructor institutions service error
   *
   * @param {*} error
   */
  // eslint-disable-next-line class-methods-use-this
  handleError(error) {
    Framework.getEventManager().publish(INSTRUCTOR_INSTITUTIONS_FAILED, { error });
  }
}
